import { useCallback } from 'react';
import ReactAsyncMultiSelect from 'react-select/async';
import ReactAsyncCreatableMultiSelect from 'react-select/async-creatable';
import { components } from 'react-select';
import classnames from 'classnames';
import isNil from 'lodash/isNil';
import type { ControlProps, MultiValueGenericProps, MultiValueRemoveProps } from 'react-select';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import '../Select_.scss';
import '../MultiSelect/MultiSelect_.scss';

import { NotValidFieldText } from '@components/NotValidFieldText';
import { FieldTitle } from '@components/FieldTitle';
import { ScrollBar } from '@components/ScrollBar';

import { OptionType, DropdownIndicator, noOptionsMessage } from '../Select';
import { MenuList } from '@components/Select/MultiSelect/components/MenuList';
import { translate } from '@chaika/i18n/helper';
import { Icon, IconType } from '@components/Icon';
import { normalizeYoFilter } from '@utils/normalizeYoString';
import { useAsyncMultiSelect } from './hooks/useAsyncMultiSelect';

// https://github.com/JedWatson/react-select#props
export interface AsyncMultiSelectProps {
    // The default set of options to show before the user starts searching. When
    // set to `true`, the results for loadOptions('') will be autoloaded.
    defaultOptions: OptionType[];
    options?: OptionType[];
    title?: string;
    name?: string;
    value?: OptionType[];
    placeholder?: string;
    isValid?: boolean;
    isLoading?: boolean;
    notValidText?: string;
    disabled?: boolean;
    clearAfterSelect?: boolean;
    isCreatable?: boolean;
    forcePlaceholder?: boolean;
    cacheOptions?: boolean;
    dataTest: string;
    menuPortalTarget?: HTMLElement | null;
    // Function that returns a promise, which is the set of options to be used
    // once the promise resolves.
    loadOptions(inputValue: string): Promise<OptionType[]> | void;
    onChange(selectedItem: Nullable<OptionType[]>, name?: string): void;
    onMenuOpen?(): void;
}

interface Props extends AsyncMultiSelectProps {
    selectedValues?: string[] | number[];
    customValues?: string[];
    onCreateOption?(value: OptionType[], name?: string): void;
}

export function AsyncMultiSelect({
    title,
    placeholder,
    dataTest,
    isValid,
    notValidText,
    isCreatable,
    name,
    disabled,
    isLoading,
    options,
    onMenuOpen,
    clearAfterSelect,
    menuPortalTarget,
    onChange,
    onCreateOption,
    selectedValues,
    customValues,
    value,
    forcePlaceholder,
    cacheOptions = true,
    defaultOptions,
    loadOptions,
}: Props) {
    const {
        value: resolvedValue,
        placeholder: resolvedPlaceholder,
        portalProps,
        inputValue,
        handleChange,
        handleCreateOption,
        handleInputChange,
        handleLoadOptions,
        handleFocus,
        handleBlur,
    } = useAsyncMultiSelect({
        defaultOptions,
        value,
        selectedValues,
        customValues,
        placeholder,
        title,
        forcePlaceholder,
        name,
        clearAfterSelect,
        menuPortalTarget,
        loadOptions,
        onChange,
        onCreateOption,
    });

    const withValidStatus = !isNil(isValid);

    const formatCreateLabel = useCallback(
        (label: string) => (
            <div>
                {translate('add')}: {label}
            </div>
        ),
        [],
    );

    const sharedProps = {
        cacheOptions,
        isMulti: true as const,
        name,
        options,
        isLoading,
        loadingMessage: () => translate('loading'),
        noOptionsMessage,
        placeholder: resolvedPlaceholder ?? '-',
        className: 'component_select__root',
        classNamePrefix: 'react-select',
        components: { Control, DropdownIndicator, MenuList, MultiValueContainer, MultiValueRemove },
        isDisabled: disabled,
        defaultOptions,
        value: resolvedValue,
        isClearable: false,
        filterOption: normalizeYoFilter,
        loadOptions: handleLoadOptions,
        inputValue,
        onInputChange: handleInputChange,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onMenuOpen,
        ...portalProps,
    };

    return (
        <div className={'component_multi_select'}>
            <div
                className={classnames(['component_select__root', 'component_multi_select__root'])}
                data-test={dataTest}
                data-testid={dataTest}
            >
                {title && (
                    <div className={classnames(['title'])}>
                        <FieldTitle title={title} />
                    </div>
                )}
                {isCreatable ? (
                    <ReactAsyncCreatableMultiSelect
                        {...sharedProps}
                        onCreateOption={handleCreateOption}
                        formatCreateLabel={formatCreateLabel}
                    />
                ) : (
                    <ReactAsyncMultiSelect {...sharedProps} />
                )}
                {withValidStatus && !isValid && notValidText && <NotValidFieldText title={notValidText} />}
            </div>
        </div>
    );
}

export function MultiValueContainer(props: MultiValueGenericProps<OptionType, true>) {
    return (
        <div className={'component_multi_select__value'}>
            <components.MultiValueContainer {...props} />
        </div>
    );
}

export function Control(props: ControlProps<OptionType, true>) {
    return (
        <div className={'component_multi_select__control'}>
            <ScrollBar autoHeight>
                <components.Control {...props} />
            </ScrollBar>
        </div>
    );
}

export function MultiValueRemove(props: MultiValueRemoveProps<OptionType, true>) {
    return (
        <components.MultiValueRemove {...props}>
            <div className={'component_multi_select__remover'}>
                <div className={'component_multi_select__icon_wrapper'}>
                    <Icon type={IconType.CROSS} />
                </div>
            </div>
        </components.MultiValueRemove>
    );
}
